import { UserImpressionType } from '@trucksmarter/shared';
import { PropsWithChildren, createContext, useContext } from 'react';
import {
  useImpression,
  UseImpressionReturn,
} from '@ui/shared/ImpressionsProvider/ImpressionsProvider';

export enum FactoringUpsellEntryPoint {
  LoadBoardBanner = 'load-board-banner',
  LoadDetailsCard = 'load-details-card',
  NavbarLinkBadge = 'navbar-link-badge',
}

const FactoringUpsellEntryPointContext =
  createContext<FactoringUpsellEntryPoint | null>(null);

export function useFactoringUpsellEntryPoint() {
  const entryPoint = useContext(FactoringUpsellEntryPointContext);

  if (entryPoint === null) {
    throw new Error(
      'useFactoringUpsellEntryPoint must be used within a FactoringUpsellEntryPointProvider',
    );
  }

  return entryPoint;
}

const ENTRY_POINT_TO_IMPRESSION_TYPE: Record<
  FactoringUpsellEntryPoint,
  UserImpressionType
> = {
  [FactoringUpsellEntryPoint.LoadBoardBanner]:
    UserImpressionType.WebAppFactoringUpsellBanner,
  [FactoringUpsellEntryPoint.LoadDetailsCard]:
    UserImpressionType.WebAppFactoringUpsellCard,
  [FactoringUpsellEntryPoint.NavbarLinkBadge]:
    UserImpressionType.WebAppFactoringUpsellNavbarLinkBadge,
};

const FactoringUpsellEntryPointImpressionContext =
  createContext<UseImpressionReturn | null>(null);

export function useFactoringUpsellEntryPointImpression() {
  const impression = useContext(FactoringUpsellEntryPointImpressionContext);

  if (impression === null) {
    throw new Error(
      'useFactoringUpsellEntryPointImpression must be used within a FactoringUpsellEntryPointProvider',
    );
  }

  return impression;
}

interface Props {
  value: FactoringUpsellEntryPoint;
  shouldHideIfEngagementRecorded?: boolean;
}

export default function FactoringUpsellEntryPointProvider({
  value,
  children,
  shouldHideIfEngagementRecorded = true,
}: PropsWithChildren<Props>) {
  const impression = useImpression(ENTRY_POINT_TO_IMPRESSION_TYPE[value]);

  return (
    <FactoringUpsellEntryPointContext.Provider value={value}>
      {(impression.data?.hasAccepted == null ||
        !shouldHideIfEngagementRecorded) && (
        <FactoringUpsellEntryPointImpressionContext.Provider value={impression}>
          {children}
        </FactoringUpsellEntryPointImpressionContext.Provider>
      )}
    </FactoringUpsellEntryPointContext.Provider>
  );
}
